import { FC, ReactNode, useState, useEffect, useRef } from 'react'
import {
  Avatar as ShadcnAvatar,
  AvatarImage as ShadcnAvatarImage,
  AvatarFallback as ShadcnAvatarFallback,
} from '@dc-shared-web/components/shadcn/avatar'
import { cva, VariantProps } from 'class-variance-authority'

const avatarStyles = cva('', {
  variants: {
    variant: {
      default: '',
      bordered: 'tw-border-2 tw-border-white',
    },
    size: {
      xs: 'tw-h-6 tw-w-6',
      sm: 'tw-h-8 tw-w-8',
      md: 'tw-h-10 tw-w-10',
      lg: 'tw-h-12 tw-w-12',
      xl: 'tw-h-14 tw-w-14',
      '2xl': 'tw-h-16 tw-w-16',
      giant: 'tw-h-40 tw-w-40',
    },
    shape: {
      circle: '',
      rounded: 'tw-rounded-md',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'md',
    shape: 'circle',
  },
})

const titleStyles = cva('tw-font-medium', {
  variants: {
    size: {
      xs: 'tw-text-sm',
      sm: 'tw-text-sm',
      md: 'tw-text-sm',
      lg: 'tw-text-base',
      xl: 'tw-text-lg',
      '2xl': 'tw-text-lg',
      giant: 'tw-text-xl',
    },
  },
  defaultVariants: {
    size: 'md',
  },
})

const descriptionStyles = cva('tw-text-neutral-500', {
  variants: {
    size: {
      xs: 'tw-text-2xs',
      sm: 'tw-text-2xs',
      md: 'tw-text-sm',
      lg: 'tw-text-sm',
      xl: 'tw-text-base',
      '2xl': 'tw-text-base',
      giant: 'tw-text-lg',
    },
  },
  defaultVariants: {
    size: 'md',
  },
})

const statusIndicatorStyles = cva(
  'tw-absolute -tw-bottom-0.5 -tw-right-0 tw-rounded-full tw-bg-success-700 tw-border-2 tw-border-white',
  {
    variants: {
      size: {
        xs: 'tw-size-3',
        sm: 'tw-size-3',
        md: 'tw-size-4',
        lg: 'tw-size-4',
        xl: 'tw-size-5',
        '2xl': 'tw-size-5',
        giant: 'tw-size-5',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
)

const fallbackStyles = cva('', {
  variants: {
    size: {
      xs: 'tw-text-3xs [&_svg]:tw-size-2.5',
      sm: 'tw-text-2xs [&_svg]:tw-size-3',
      md: 'tw-text-xs [&_svg]:tw-size-3.5',
      lg: 'tw-text-lg [&_svg]:tw-size-4.5',
      xl: 'tw-text-lg [&_svg]:tw-size-4.5',
      '2xl': 'tw-text-lg [&_svg]:tw-size-4.5',
      giant: 'tw-text-2xl [&_svg]:tw-size-10',
    },
    color: {
      neutral:
        'tw-bg-neutral-100 tw-text-neutral-500 [&_svg]:tw-text-neutral-500',
      blue: 'tw-bg-primary-200 tw-text-primary-900 [&_svg]:tw-text-primary-900',
      green:
        'tw-bg-success-100 tw-text-success-950 [&_svg]:tw-text-success-950',
      purple: 'tw-bg-purple-100 tw-text-purple-900 [&_svg]:tw-text-purple-900',
    },
    shape: {
      circle: '',
      rounded: 'tw-rounded-md',
    },
  },
  defaultVariants: {
    size: 'md',
    color: 'neutral',
    shape: 'circle',
  },
})

const shimmerStyles = cva(
  'tw-h-full tw-w-full tw-absolute tw-inset-0 tw-animate-skeleton-shimmer',
  {
    variants: {
      shape: {
        circle: 'tw-rounded-full',
        rounded: 'tw-rounded-md',
      },
    },
    defaultVariants: {
      shape: 'circle',
    },
  }
)

export type AvatarProps = {
  /** Accessible label for the avatar. If not provided, will use the title prop or fall back to "Avatar" */
  'aria-label'?: string
  /** Optional description text shown below the title. */
  description?: string
  /** Element (string or ReactNode if it's an icon) to show when image fails to load or is loading. */
  fallback?: ReactNode
  /** Style of the fallback. */
  fallbackColor?: VariantProps<typeof fallbackStyles>['color']
  /** Shape of the avatar. */
  shape?: 'circle' | 'rounded'
  /** Whether to show a white border around the avatar. */
  showBorder?: boolean
  /** Whether to show an online status indicator. */
  showStatusIndicator?: boolean
  /** Size of the avatar, defaults to 'md'. */
  size?: VariantProps<typeof avatarStyles>['size']
  /** URL of the avatar image. */
  src?: string | undefined | null
  /** Optional title text shown next to avatar. */
  title?: string
}

export const Avatar: FC<AvatarProps> = ({
  description,
  fallback,
  fallbackColor,
  shape = 'circle',
  showBorder,
  showStatusIndicator,
  size = 'md',
  src,
  title,
  'aria-label': ariaLabel,
}) => {
  const [isLoading, setIsLoading] = useState(!!src)
  const [hasError, setHasError] = useState(false)
  /*
    Ref to store the timeout for image loading fallback.
    Used to trigger an error state if the image does not load or error events do not fire within a set time.
  */
  const timeoutRef = useRef<NodeJS.Timeout>()

  useEffect(() => {
    if (src) {
      setIsLoading(true)
      setHasError(false)

      timeoutRef.current = setTimeout(
        () => {
          setIsLoading(false)
          setHasError(true)
        },
        /* 5 seconds timeout to handle cases where onError doesn't fire. */
        5000
      )
    } else {
      setIsLoading(false)
      setHasError(false)
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [src])

  const handleImageLoad = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    setIsLoading(false)
    setHasError(false)
  }

  const handleImageError = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    setIsLoading(false)
    setHasError(true)
  }

  const avatarLabel = ariaLabel || title || 'Avatar'

  const showFallback = (!src && !isLoading && fallback) || hasError
  const showLoadingState = isLoading && !hasError
  const showAvatarInfo = !!title || !!description

  return (
    <div className='tw-flex tw-gap-2 tw-items-center tw-justify-center'>
      <div className='tw-relative'>
        <ShadcnAvatar
          className={avatarStyles({
            variant: showBorder ? 'bordered' : 'default',
            size,
            shape,
          })}
          aria-label={avatarLabel}
        >
          {src && (
            <ShadcnAvatarImage
              src={src}
              onLoad={handleImageLoad}
              onError={handleImageError}
            />
          )}
          {showFallback && (
            <ShadcnAvatarFallback
              className={fallbackStyles({ size, color: fallbackColor, shape })}
            >
              {fallback}
            </ShadcnAvatarFallback>
          )}
        </ShadcnAvatar>
        {showLoadingState && <div className={shimmerStyles({ shape })} />}
        {showStatusIndicator && (
          <div className={statusIndicatorStyles({ size })} />
        )}
      </div>
      {showAvatarInfo && (
        <div>
          {title && <div className={titleStyles({ size })}>{title}</div>}
          {description && (
            <div className={descriptionStyles({ size })}>{description}</div>
          )}
        </div>
      )}
    </div>
  )
}
